/* Seller's per-buyer pricing control. Lives inside Seller Settings →
 * Buyer pricing. Two sub-views:
 *   1. Groups list — create / edit / delete tiers (% off or cost+%)
 *   2. SKU overrides — exact-$ contract prices per (buyer, SKU)
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import {
  Plus, Pencil, Trash2, Tag, Users, DollarSign, Layers, Loader2, AlertCircle,
} from "lucide-react";

export default function BuyerPricingPanel() {
  const [tab, setTab] = useState("groups");
  return (
    <div className="space-y-4" data-testid="buyer-pricing-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="display text-xl">Buyer pricing</h2>
          <p className="text-[12.5px] text-foreground/65">
            Set discounts per buyer tier (e.g. VIP gets 15% off) — apply overrides per SKU for strategic deals.
          </p>
        </div>
        <div className="inline-flex gap-1 text-xs">
          {[{id:"groups",l:"Groups",I:Layers},{id:"overrides",l:"SKU overrides",I:Tag}].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`bp-tab-${t.id}`}
              className={`inline-flex items-center gap-1.5 h-9 px-3.5 rounded-full ${tab === t.id ? "bg-foreground text-background" : "text-foreground/65 hover:text-foreground"}`}>
              <t.I className="w-3.5 h-3.5"/>{t.l}
            </button>
          ))}
        </div>
      </div>

      {tab === "groups"    && <GroupsPanel/>}
      {tab === "overrides" && <OverridesPanel/>}
    </div>
  );
}

function GroupsPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const refresh = () => {
    setLoading(true);
    api.get("/seller/buyer-groups").then(r => setRows(r.data?.data || [])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const del = async (g) => {
    if (!window.confirm(`Delete group "${g.name}"? All ${g.buyer_count || 0} buyer(s) will move to Standard.`)) return;
    try { await api.delete(`/seller/buyer-groups/${g.id}`); toast.success("Group deleted"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="py-12 text-center text-foreground/40"><Loader2 className="w-5 h-5 animate-spin inline mr-2"/>Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={() => setEditing({})} data-testid="bp-new-group"
          className="rounded-full bg-foreground text-background h-9 text-xs px-4">
          <Plus className="w-3.5 h-3.5 mr-1.5"/>New group
        </Button>
      </div>
      <div className="rounded-xl border border-border bg-white overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-foreground/55">
            <tr><Th>Name</Th><Th>Discount</Th><Th>Buyers</Th><Th right>Actions</Th></tr>
          </thead>
          <tbody>
            {rows.map(g => (
              <tr key={g.id} className="border-t border-border" data-testid={`bp-group-${g.id}`}>
                <Td>
                  <div className="font-medium">{g.name}</div>
                  {g.is_default && <span className="chip bg-secondary text-[9.5px] mt-0.5">default</span>}
                </Td>
                <Td>
                  {g.discount_type === "percent_off"
                    ? <span className="mono">{g.discount_value}% off list</span>
                    : <span className="mono">cost + {g.discount_value}%</span>}
                </Td>
                <Td><span className="mono">{g.buyer_count}</span></Td>
                <Td right>
                  <button onClick={() => setEditing(g)} className="text-foreground/65 hover:text-foreground mr-3" data-testid={`bp-edit-${g.id}`}>
                    <Pencil className="w-3.5 h-3.5"/>
                  </button>
                  {!g.is_default && (
                    <button onClick={() => del(g)} className="text-rose-600" data-testid={`bp-delete-${g.id}`}>
                      <Trash2 className="w-3.5 h-3.5"/>
                    </button>
                  )}
                </Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={4} className="text-center text-foreground/40 py-10">No groups</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <GroupModal initial={editing} onClose={() => setEditing(null)} onSaved={refresh}/>}
    </div>
  );
}

function GroupModal({ initial, onClose, onSaved }) {
  const [name, setName]   = useState(initial?.name || "");
  const [type, setType]   = useState(initial?.discount_type || "percent_off");
  const [value, setValue] = useState(initial?.discount_value ?? 0);
  const [busy, setBusy]   = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const body = { name, discount_type: type, discount_value: parseFloat(value) || 0 };
      if (initial?.id) await api.put(`/seller/buyer-groups/${initial.id}`, body);
      else            await api.post(`/seller/buyer-groups`, body);
      toast.success("Saved");
      onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bp-group-modal">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit group" : "New buyer group"}</DialogTitle>
          <DialogDescription>Buyers in this group get the discount on every product unless a SKU override applies.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Group name</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="VIP" data-testid="bp-group-name"/></div>
          <div>
            <Label>Discount type</Label>
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="bp-group-type"
              className="mt-1.5 w-full h-10 rounded-md border border-input bg-white px-3 text-sm">
              <option value="percent_off">% off list price</option>
              <option value="margin_over_cost">Cost + % markup</option>
            </select>
            <div className="text-[10.5px] text-foreground/50 mt-1">
              {type === "percent_off" ? "Buyer pays list price minus this %." : "Buyer pays your cost + this %. Requires `cost` field on products."}
            </div>
          </div>
          <div>
            <Label>Value (%)</Label>
            <Input type="number" value={value} onChange={(e) => setValue(e.target.value)} placeholder="15" data-testid="bp-group-value"/>
          </div>
          <Button onClick={save} disabled={busy || !name} data-testid="bp-group-save"
            className="w-full rounded-full bg-foreground text-background h-10">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : null}Save group
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function OverridesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const refresh = () => {
    setLoading(true);
    api.get("/seller/buyer-price-overrides").then(r => setRows(r.data?.data || [])).finally(() => setLoading(false));
  };
  useEffect(refresh, []);

  const del = async (oid) => {
    if (!window.confirm("Remove this override?")) return;
    try { await api.delete(`/seller/buyer-price-overrides/${oid}`); toast.success("Removed"); refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11.5px] text-amber-900">
        <AlertCircle className="inline w-3.5 h-3.5 mr-1"/>
        SKU overrides win over group discounts. Use sparingly — for contract deals or strategic accounts.
      </div>
      <div className="flex justify-end">
        <Button onClick={() => setCreating(true)} data-testid="bp-new-override"
          className="rounded-full bg-foreground text-background h-9 text-xs px-4">
          <Plus className="w-3.5 h-3.5 mr-1.5"/>Add override
        </Button>
      </div>
      {loading ? <div className="py-12 text-center text-foreground/40"><Loader2 className="w-5 h-5 animate-spin inline"/></div> : (
        <div className="rounded-xl border border-border bg-white overflow-x-auto">
          <table className="w-full text-[12px]">
            <thead className="bg-secondary/40 text-[10px] uppercase tracking-wider text-foreground/55">
              <tr><Th>Buyer ID</Th><Th>SKU</Th><Th>Override price</Th><Th>Expires</Th><Th>Note</Th><Th right>Action</Th></tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} className="border-t border-border" data-testid={`bp-ovr-${r.id}`}>
                  <Td><span className="mono text-[10.5px]">{r.buyer_id?.slice(0,8)}…</span></Td>
                  <Td><span className="mono">{r.sku}</span></Td>
                  <Td><span className="mono">${Number(r.override_price).toFixed(2)}</span></Td>
                  <Td>{r.expires_at || <span className="text-foreground/40">—</span>}</Td>
                  <Td><span className="text-[10.5px] text-foreground/65 italic">{r.note || "—"}</span></Td>
                  <Td right><button onClick={() => del(r.id)} className="text-rose-600" data-testid={`bp-ovr-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5"/></button></Td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="text-center text-foreground/40 py-10">No overrides</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {creating && <OverrideModal onClose={() => setCreating(false)} onSaved={refresh}/>}
    </div>
  );
}

function OverrideModal({ onClose, onSaved }) {
  const [buyerId, setBuyerId] = useState("");
  const [sku, setSku]         = useState("");
  const [price, setPrice]     = useState("");
  const [note, setNote]       = useState("");
  const [busy, setBusy]       = useState(false);
  const save = async () => {
    if (!buyerId || !sku || !price) { toast.error("buyer_id, sku, price all required"); return; }
    setBusy(true);
    try {
      await api.post("/seller/buyer-price-overrides", {
        buyer_id: buyerId, sku, override_price: parseFloat(price), note,
      });
      toast.success("Override saved");
      onClose(); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="bp-ovr-modal">
        <DialogHeader>
          <DialogTitle>New SKU price override</DialogTitle>
          <DialogDescription>Set an exact price for one (buyer, SKU) pair. Overrides their group discount.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Buyer ID</Label><Input value={buyerId} onChange={(e) => setBuyerId(e.target.value)} className="mono text-[11.5px]" data-testid="bp-ovr-buyer"/></div>
          <div><Label>SKU</Label><Input value={sku} onChange={(e) => setSku(e.target.value)} className="mono" data-testid="bp-ovr-sku"/></div>
          <div><Label>Override price ($)</Label><Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} className="mono" data-testid="bp-ovr-price"/></div>
          <div><Label>Note (optional)</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Q1 contract" data-testid="bp-ovr-note"/></div>
          <Button onClick={save} disabled={busy} data-testid="bp-ovr-save"
            className="w-full rounded-full bg-foreground text-background h-10">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : null}Save override
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Th({ children, right }) { return <th className={`px-3 py-2 font-medium ${right ? "text-right" : "text-left"}`}>{children}</th>; }
function Td({ children, right }) { return <td className={`px-3 py-2.5 align-middle ${right ? "text-right" : ""}`}>{children}</td>; }
